import type {
	BurnupChartData,
	SprintEndData,
	SprintScopeChangeData,
	SprintStartData,
} from '../../types';

/**
 * This is an aggregate of event types and start/end so that we can draw the line.
 */
export type SprintEvent =
	| {
			type: 'scope';
			data: SprintScopeChangeData;
	  }
	| {
			type: 'start';
			data: SprintStartData;
	  }
	| {
			type: 'today';
			data: {
				timestamp: string;
				completion: number;
				scope: number;
			};
	  }
	| {
			type: 'end';
			data: SprintEndData;
	  };

/**
 * Get the timestamp for a `SprintEvent`
 */
export const getTimestamp = ({ data: { timestamp } }: SprintEvent): string => timestamp;

/**
 * Get the completion value at a point in time
 */
export const getCompletion = (anyEvent: SprintEvent): number => {
	if (anyEvent.type === 'start') {
		return 0;
	}

	if (anyEvent.type === 'end') {
		return anyEvent.data.completion;
	}

	return anyEvent.data.completion;
};

export const getScope = (anyEvent: SprintEvent): number => {
	if (anyEvent.type === 'start') {
		return anyEvent.data.scopeEstimate;
	}
	return anyEvent.data.scope;
};

export const getTrailingEvent = (
	now: Date,
	{ sprintEndEvent, scopeChangeEvents, sprintStartEvent }: BurnupChartData,
): SprintEvent[] => {
	if (sprintEndEvent) {
		return [{ type: 'end', data: sprintEndEvent }];
	}

	const lastEvent = scopeChangeEvents[scopeChangeEvents.length - 1];
	if (lastEvent && new Date(lastEvent.timestamp).getTime() > now.getTime()) {
		return [];
	}

	return [
		{
			type: 'today',
			data: {
				timestamp: now.toISOString(),
				completion: lastEvent ? lastEvent.completion : 0,
				scope: lastEvent ? lastEvent.scope : sprintStartEvent.scopeEstimate,
			},
		},
	];
};

/**
 * Merge burnup structure into a flat list
 */
export const buildEvents = (now: Date, burnup: BurnupChartData): SprintEvent[] => [
	{ type: 'start', data: burnup.sprintStartEvent },
	// @ts-expect-error - TS2322 - Type '{ type: string; data: SprintScopeChangeData; }' is not assignable to type 'SprintEvent'.
	...burnup.scopeChangeEvents.map((event) => ({ type: 'scope', data: event })),
	// The last event is either the end event or the current date.
	// @ts-expect-error - TS2322 - Type 'SprintEvent | { type: string; data: SprintScopeChangeData; }' is not assignable to type 'SprintEvent'.
	...getTrailingEvent(now, burnup),
];
